import { useCallback, useRef, useState } from 'react';

import {
  TCreateProgramBroadcast,
  createProgramBroadcast,
} from '@/features/broadcast-schedule/api/post-program-broadcast';
import { broadcastScheduleQueryKeys } from '@/features/broadcast-schedule/api/query-keys';
import { queryClient } from '@/lib/react-query';

import { TProgram } from '../types';
import { TProgramBroadcastCandidate } from '../utils/programBroadcastCandidates';

export type TFailedProgramBroadcast = {
  candidate: TProgramBroadcastCandidate;
  error: unknown;
};

export type TProgramBroadcastBatchResult = {
  createdCount: number;
  failed: TFailedProgramBroadcast[];
  /** The run was stopped by the user, so the emissions after it were never attempted. */
  isCancelled: boolean;
};

export type TProgramBroadcastBatchProgress = {
  done: number;
  total: number;
};

/**
 * Maps a candidate onto the emission payload, taking every field the user is
 * not asked for from the programme — the same mapping the Parrilla's drawer
 * applies when a programme is selected. `toISOString()` happens here and
 * nowhere earlier: the candidate's instants stay local wall-clock Dates until
 * the payload boundary.
 *
 * `review_state` is deliberately absent: the server applies its own default,
 * exactly as it does for an emission created by hand.
 */
function toCreatePayload(
  candidate: TProgramBroadcastCandidate,
  program: TProgram
): TCreateProgramBroadcast {
  return {
    name: program.name,
    description: program.description,
    start_datetime: candidate.startDatetime.toISOString(),
    end_datetime: candidate.endDatetime.toISOString(),
    delivery_datetime: candidate.deliveryDatetime?.toISOString() ?? null,
    channel_id: program.channel.id,
    language_id: program.language_id,
    priority: program.priority,
    program_id: program.id,
    audience_program_source_id: program.audience_program_source_id,
  };
}

/**
 * Creates the emissions one request at a time through the existing endpoint.
 *
 * It drives the raw `createProgramBroadcast` rather than `useCreateProgramBroadcast`
 * on purpose: that hook fires a toast and invalidates the whole broadcast-schedule
 * cache per mutation, which for forty emissions is forty toasts and forty
 * refetches. The batch reports once, at the end, and invalidates once.
 *
 * A failure never stops the run — it is collected with its candidate so the
 * modal can say exactly which emissions are missing.
 *
 * `cancel` is the user's way out: it aborts the request in flight and stops
 * before the next one, so a server that never answers cannot leave the modal
 * waiting on a loop with no end. What it already created stays created, and
 * the report says the run was cancelled — regenerating the same period creates
 * only what is still missing.
 *
 * One run at a time: a second `run` while one is in flight is refused rather
 * than allowed to overwrite the first one's progress and report.
 */
export function useProgramBroadcastBatch() {
  const [isRunning, setIsRunning] = useState(false);
  const [progress, setProgress] = useState<TProgramBroadcastBatchProgress | null>(null);
  const [result, setResult] = useState<TProgramBroadcastBatchResult | null>(null);
  const abortRef = useRef<AbortController | null>(null);

  const cancel = useCallback(() => {
    abortRef.current?.abort();
  }, []);

  const reset = useCallback(() => {
    abortRef.current?.abort();
    // Dropping the controller is what tells a run still unwinding that it is no
    // longer the current one, so its report cannot land after this reset.
    abortRef.current = null;
    setIsRunning(false);
    setProgress(null);
    setResult(null);
  }, []);

  const run = useCallback(
    async (
      candidates: TProgramBroadcastCandidate[],
      program: TProgram
    ): Promise<TProgramBroadcastBatchResult> => {
      // A run already in flight owns the state a second one would overwrite,
      // and its loop would keep creating emissions behind it. The UI does not
      // offer the second run, and this makes the hook say so on its own.
      if (abortRef.current) return { createdCount: 0, failed: [], isCancelled: true };

      const abortController = new AbortController();

      abortRef.current = abortController;

      setIsRunning(true);
      setResult(null);
      setProgress({ done: 0, total: candidates.length });

      const failed: TFailedProgramBroadcast[] = [];
      let createdCount = 0;

      for (const candidate of candidates) {
        if (abortController.signal.aborted) break;

        try {
          // Sequential on purpose: see the hook's doc comment.
          await createProgramBroadcast(toCreatePayload(candidate, program), {
            signal: abortController.signal,
          });
          createdCount += 1;
        } catch (error) {
          // The aborted request is not a failure the user has to read: it is
          // the cancellation they just asked for, and whether it reached the
          // server is what regenerating the period settles.
          if (abortController.signal.aborted) break;

          failed.push({ candidate, error });
        }

        setProgress(current =>
          current ? { ...current, done: current.done + 1 } : { done: 1, total: candidates.length }
        );
      }

      queryClient.invalidateQueries({ queryKey: broadcastScheduleQueryKeys.all });

      const batchResult = {
        createdCount,
        failed,
        isCancelled: abortController.signal.aborted,
      };

      // A reset while the loop was unwinding already cleared the modal, so this
      // run is no longer the one on screen and must not repaint it.
      if (abortRef.current === abortController) {
        abortRef.current = null;

        setResult(batchResult);
        setIsRunning(false);
      }

      return batchResult;
    },
    []
  );

  return { run, cancel, reset, isRunning, progress, result };
}
